import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import type { v4 } from '@rotra-air-ocean/rotranext-shared-code';
import {
  animate as animateMotion,
  AnimatePresence,
  motion,
  useMotionValue,
  useTransform,
} from 'framer-motion';
import { Check, Heart, ImageOff, RotateCcw, X } from 'lucide-react';

import {
  type ImageFingerprintListItem,
  MailAutomationAPI,
} from '$api/MailAutomationAPI';

type ReviewDecision = 'ignored' | 'allowed';
type ExitDirection = 'left' | 'right';

interface LastAction {
  decision: ReviewDecision;
  item: ImageFingerprintListItem;
}

interface PreviewState {
  status: 'loading' | 'ready' | 'error';
  url?: string;
}

interface ImageFingerprintQuickReviewDialogProps {
  actor: v4.WorkflowActor | null;
  minOccurrences?: number;
  onClose: () => void;
  reasonLabels: Readonly<Record<string, string>>;
  search?: string;
  token: string;
}

const decisionDirection: Record<ReviewDecision, ExitDirection> = {
  ignored: 'left',
  allowed: 'right',
};

const SwipeCard = ({
  disabled,
  exitDirection,
  item,
  onDecision,
  preview,
  reasonLabels,
}: {
  disabled: boolean;
  exitDirection: ExitDirection | null;
  item: ImageFingerprintListItem;
  onDecision: (decision: ReviewDecision) => void;
  preview?: PreviewState;
  reasonLabels: Readonly<Record<string, string>>;
}) => {
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-240, 0, 240], [-16, 0, 16]);
  const ignoreOpacity = useTransform(x, [-150, -28], [1, 0]);
  const allowOpacity = useTransform(x, [28, 150], [0, 1]);
  const name = item.sample?.name ?? 'Image sample unavailable';
  let previewContent = <CircularProgress size={32} />;

  if (preview?.status === 'ready' && preview.url) {
    previewContent = (
      <Box
        component="img"
        src={preview.url}
        alt={name}
        draggable={false}
        sx={{
          height: '100%',
          objectFit: 'contain',
          width: '100%',
        }}
      />
    );
  } else if (!item.sample || preview?.status === 'error') {
    previewContent = (
      <Stack
        spacing={1}
        sx={{
          alignItems: 'center',
          color: 'text.secondary',
        }}
      >
        <ImageOff size={36} />
        <Typography variant="body2">Preview unavailable</Typography>
      </Stack>
    );
  }

  useEffect(() => {
    let target = 0;
    if (disabled && exitDirection) {
      target = exitDirection === 'left' ? -72 : 72;
    }
    const animation = animateMotion(x, target, {
      damping: 24,
      stiffness: 320,
      type: 'spring',
    });
    return () => animation.stop();
  }, [disabled, exitDirection, x]);

  return (
    <motion.div
      key={item.fingerprintId}
      drag={disabled ? false : 'x'}
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.9}
      onDragEnd={(_event, info) => {
        if (disabled) {
          return;
        }
        let direction = 0;
        if (Math.abs(info.offset.x) >= 120) {
          direction = info.offset.x;
        } else if (Math.abs(info.velocity.x) >= 700) {
          direction = info.velocity.x;
        }
        if (direction < 0) {
          onDecision('ignored');
        } else if (direction > 0) {
          onDecision('allowed');
        }
      }}
      initial={{ opacity: 0, scale: 0.96, y: 18 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{
        opacity: 0,
        rotate: exitDirection === 'left' ? -24 : 24,
        x: exitDirection === 'left' ? -900 : 900,
        y: 24,
      }}
      transition={{ type: 'spring', stiffness: 340, damping: 26 }}
      style={{
        cursor: disabled ? 'wait' : 'grab',
        maxWidth: 560,
        position: 'absolute',
        rotate,
        touchAction: 'pan-y',
        width: 'calc(100% - 32px)',
        x,
        zIndex: 1,
      }}
      whileDrag={{ cursor: 'grabbing', scale: 1.025 }}
    >
      <Card
        elevation={12}
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 4,
          overflow: 'hidden',
          position: 'relative',
          userSelect: 'none',
        }}
      >
        <motion.div
          style={{
            border: '4px solid currentColor',
            borderRadius: 8,
            color: '#d32f2f',
            fontSize: 30,
            fontWeight: 900,
            left: 24,
            letterSpacing: 2,
            lineHeight: 1,
            opacity: ignoreOpacity,
            padding: '6px 10px',
            pointerEvents: 'none',
            position: 'absolute',
            rotate: '-12deg',
            top: 28,
            zIndex: 2,
          }}
        >
          IGNORE
        </motion.div>
        <motion.div
          style={{
            border: '4px solid currentColor',
            borderRadius: 8,
            color: '#2e7d32',
            fontSize: 30,
            fontWeight: 900,
            letterSpacing: 2,
            lineHeight: 1,
            opacity: allowOpacity,
            padding: '6px 10px',
            pointerEvents: 'none',
            position: 'absolute',
            right: 24,
            rotate: '12deg',
            top: 28,
            zIndex: 2,
          }}
        >
          ALLOW
        </motion.div>

        <Box
          sx={{
            alignItems: 'center',
            bgcolor: 'background.default',
            display: 'flex',
            height: { xs: 'min(52vh, 430px)', sm: 'min(56vh, 520px)' },
            justifyContent: 'center',
            minHeight: 260,
            p: 2,
          }}
        >
          {previewContent}
        </Box>

        <Stack spacing={1.25} sx={{ p: 2 }}>
          <Tooltip title={name}>
            <Typography variant="h6" noWrap>
              {name}
            </Typography>
          </Tooltip>
          <Stack
            direction="row"
            sx={{
              gap: 0.75,
              flexWrap: 'wrap',
            }}
          >
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              label={`${item.occurrenceCount} occurrences`}
            />
            {item.suggestionReasons.map((reason) => (
              <Chip
                key={reason}
                size="small"
                variant="outlined"
                label={reasonLabels[reason] ?? reason}
              />
            ))}
          </Stack>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
            }}
          >
            Suggestion score {item.suggestionScore}
          </Typography>
        </Stack>
      </Card>
    </motion.div>
  );
};

export default function ImageFingerprintQuickReviewDialog({
  actor,
  minOccurrences,
  onClose,
  reasonLabels,
  search,
  token,
}: ImageFingerprintQuickReviewDialogProps) {
  const [items, setItems] = useState<ImageFingerprintListItem[]>([]);
  const [loadingQueue, setLoadingQueue] = useState(true);
  const [saving, setSaving] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const [empty, setEmpty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastAction, setLastAction] = useState<LastAction | null>(null);
  const [exitDirection, setExitDirection] = useState<ExitDirection | null>(
    null,
  );
  const [, setPreviewRevision] = useState(0);
  const itemsRef = useRef<ImageFingerprintListItem[]>([]);
  const fetchingRef = useRef(false);
  const actionInFlightRef = useRef(false);
  const reviewedIdsRef = useRef(new Set<string>());
  const previewCacheRef = useRef(new Map<string, PreviewState>());
  const disposedRef = useRef(false);

  const replaceItems = useCallback((nextItems: ImageFingerprintListItem[]) => {
    itemsRef.current = nextItems;
    setItems(nextItems);
  }, []);

  const fetchQueue = useCallback(
    async (replace = false) => {
      if (fetchingRef.current) {
        return;
      }
      fetchingRef.current = true;
      setLoadingQueue(true);
      setError(null);
      try {
        const response = await MailAutomationAPI.getImageFingerprints(token, {
          view: 'suggested',
          search,
          minOccurrences,
          page: 0,
          rowsPerPage: 100,
        });
        const currentItems = replace ? [] : itemsRef.current;
        const existingIds = new Set(
          currentItems.map((item) => item.fingerprintId),
        );
        const freshItems = response.items.filter(
          (item) =>
            !reviewedIdsRef.current.has(item.fingerprintId) &&
            !existingIds.has(item.fingerprintId),
        );
        const nextItems = [...currentItems, ...freshItems];
        replaceItems(nextItems);
        setEmpty(nextItems.length === 0);
      } catch (queueError) {
        setError(String(queueError));
      } finally {
        fetchingRef.current = false;
        setLoadingQueue(false);
      }
    },
    [minOccurrences, replaceItems, search, token],
  );

  useEffect(() => {
    void fetchQueue(true);
  }, [fetchQueue]);

  useEffect(() => {
    const fingerprintIds = items
      .slice(0, 2)
      .filter((item) => item.sample)
      .map((item) => item.fingerprintId);

    for (const fingerprintId of fingerprintIds) {
      if (previewCacheRef.current.has(fingerprintId)) {
        continue;
      }
      previewCacheRef.current.set(fingerprintId, { status: 'loading' });
      void MailAutomationAPI.getImageFingerprintPreview(token, fingerprintId)
        .then((blob) => {
          const url = URL.createObjectURL(blob);
          if (disposedRef.current) {
            URL.revokeObjectURL(url);
            return;
          }
          previewCacheRef.current.set(fingerprintId, {
            status: 'ready',
            url,
          });
          setPreviewRevision((revision) => revision + 1);
        })
        .catch(() => {
          if (!disposedRef.current) {
            previewCacheRef.current.set(fingerprintId, { status: 'error' });
            setPreviewRevision((revision) => revision + 1);
          }
        });
    }
  }, [items, token]);

  useEffect(
    () => () => {
      disposedRef.current = true;
      for (const preview of previewCacheRef.current.values()) {
        if (preview.url) {
          URL.revokeObjectURL(preview.url);
        }
      }
      previewCacheRef.current.clear();
    },
    [],
  );

  const applyDecision = useCallback(
    async (decision: ReviewDecision) => {
      const item = itemsRef.current[0];
      if (!item || actionInFlightRef.current) {
        return;
      }

      actionInFlightRef.current = true;
      setSaving(true);
      setError(null);
      setExitDirection(decisionDirection[decision]);
      try {
        const response = await MailAutomationAPI.putImageFingerprintDecisions(
          token,
          [
            {
              fingerprintId: item.fingerprintId,
              decision,
              scope: 'inline',
            },
          ],
          actor,
        );
        if (response.succeeded !== 1) {
          throw new Error(
            response.results[0]?.message ?? 'The image could not be updated',
          );
        }

        reviewedIdsRef.current.add(item.fingerprintId);
        setLastAction({ decision, item });
        const remainingItems = itemsRef.current.slice(1);
        replaceItems(remainingItems);
        setEmpty(false);
        if (remainingItems.length <= 10) {
          void fetchQueue();
        }
      } catch (decisionError) {
        setError(String(decisionError));
        setExitDirection(null);
      } finally {
        actionInFlightRef.current = false;
        setSaving(false);
      }
    },
    [actor, fetchQueue, replaceItems, token],
  );

  const undoLastAction = useCallback(async () => {
    if (!lastAction || actionInFlightRef.current) {
      return;
    }

    actionInFlightRef.current = true;
    setUndoing(true);
    setError(null);
    try {
      const response = await MailAutomationAPI.putImageFingerprintDecisions(
        token,
        [
          {
            fingerprintId: lastAction.item.fingerprintId,
            decision: 'reset',
            scope: 'inline',
          },
        ],
        actor,
      );
      if (response.succeeded !== 1) {
        throw new Error(
          response.results[0]?.message ?? 'The decision could not be undone',
        );
      }

      reviewedIdsRef.current.delete(lastAction.item.fingerprintId);
      replaceItems([
        lastAction.item,
        ...itemsRef.current.filter(
          (item) => item.fingerprintId !== lastAction.item.fingerprintId,
        ),
      ]);
      setLastAction(null);
      setExitDirection(null);
      setEmpty(false);
    } catch (undoError) {
      setError(String(undoError));
    } finally {
      actionInFlightRef.current = false;
      setUndoing(false);
    }
  }, [actor, lastAction, replaceItems, token]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.repeat || saving || undoing) {
        return;
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        void applyDecision('ignored');
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        void applyDecision('allowed');
      }
    };
    globalThis.addEventListener('keydown', handleKeyDown);
    return () => globalThis.removeEventListener('keydown', handleKeyDown);
  }, [applyDecision, saving, undoing]);

  const currentItem = items[0];
  const busy = saving || undoing;
  const filterDescription = [
    minOccurrences ? `${minOccurrences}+ occurrences` : null,
    search ? `matching “${search}”` : null,
  ]
    .filter(Boolean)
    .join(' · ');
  let reviewContent = null;

  if (currentItem) {
    reviewContent = (
      <AnimatePresence custom={exitDirection} mode="popLayout">
        <SwipeCard
          key={currentItem.fingerprintId}
          disabled={busy}
          exitDirection={exitDirection}
          item={currentItem}
          onDecision={(decision) => void applyDecision(decision)}
          preview={previewCacheRef.current.get(currentItem.fingerprintId)}
          reasonLabels={reasonLabels}
        />
      </AnimatePresence>
    );
  } else if (loadingQueue) {
    reviewContent = <CircularProgress size={36} />;
  } else if (error) {
    reviewContent = (
      <Button variant="outlined" onClick={() => void fetchQueue(true)}>
        Retry loading suggestions
      </Button>
    );
  } else if (empty) {
    reviewContent = (
      <Stack
        spacing={1.5}
        sx={{
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <Check size={48} />
        <Typography variant="h5">
          No suggestions left for this filter
        </Typography>
        <Button variant="outlined" onClick={onClose}>
          Back to ignored images
        </Button>
      </Stack>
    );
  }

  return (
    <Dialog
      fullScreen
      open
      onClose={() => {
        if (!busy) {
          onClose();
        }
      }}
    >
      <DialogTitle
        sx={{
          alignItems: 'center',
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          gap: 2,
          justifyContent: 'space-between',
          py: 1.5,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6">Quick image review</Typography>
          <Typography
            variant="caption"
            noWrap
            sx={{
              color: 'text.secondary',
            }}
          >
            Swipe left to ignore · Swipe right to allow
            {filterDescription ? ` · ${filterDescription}` : ''}
          </Typography>
        </Box>
        <IconButton
          aria-label="Close quick review"
          disabled={busy}
          onClick={onClose}
        >
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          bgcolor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          p: { xs: 1, sm: 2 },
        }}
      >
        {error && (
          <Alert
            severity="error"
            sx={{ alignSelf: 'center', maxWidth: 720, mt: 1, width: '100%' }}
          >
            {error}
          </Alert>
        )}

        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            flex: 1,
            justifyContent: 'center',
            minHeight: 0,
            position: 'relative',
          }}
        >
          {currentItem && items.length > 1 && (
            <>
              <Box
                aria-hidden
                sx={{
                  bgcolor: 'background.paper',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 4,
                  boxShadow: 1,
                  height: { xs: 'min(62vh, 520px)', sm: 'min(68vh, 620px)' },
                  left: '50%',
                  maxWidth: 560,
                  opacity: 0.45,
                  position: 'absolute',
                  top: '50%',
                  transform:
                    'translate(-50%, -50%) translateY(28px) scale(0.91)',
                  width: 'calc(100% - 32px)',
                }}
              />
              <Box
                aria-hidden
                sx={{
                  bgcolor: 'background.paper',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 4,
                  boxShadow: 2,
                  height: { xs: 'min(62vh, 520px)', sm: 'min(68vh, 620px)' },
                  left: '50%',
                  maxWidth: 560,
                  opacity: 0.75,
                  position: 'absolute',
                  top: '50%',
                  transform:
                    'translate(-50%, -50%) translateY(16px) scale(0.955)',
                  width: 'calc(100% - 32px)',
                }}
              />
            </>
          )}
          {reviewContent}
        </Box>

        <Stack
          direction="row"
          spacing={{ xs: 2.5, sm: 4 }}
          sx={{
            justifyContent: 'center',
            alignItems: 'flex-start',
            pb: { xs: 1, sm: 2 },
            pt: 1,
          }}
        >
          <Stack
            spacing={0.5}
            sx={{
              alignItems: 'center',
            }}
          >
            <Tooltip title="Ignore (Left arrow)">
              <span>
                <IconButton
                  aria-label="Ignore image (Left arrow)"
                  color="error"
                  disabled={!currentItem || busy}
                  onClick={() => void applyDecision('ignored')}
                  sx={{
                    bgcolor: 'background.paper',
                    border: 2,
                    borderColor: 'error.main',
                    boxShadow: 4,
                    height: { xs: 58, sm: 68 },
                    transition:
                      'transform 160ms ease, background-color 160ms ease',
                    width: { xs: 58, sm: 68 },
                    '&:hover': {
                      bgcolor: 'error.main',
                      color: 'error.contrastText',
                      transform: 'scale(1.08)',
                    },
                  }}
                >
                  {saving && exitDirection === 'left' ? (
                    <CircularProgress color="inherit" size={24} />
                  ) : (
                    <X size={32} strokeWidth={2.6} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
              }}
            >
              Ignore
            </Typography>
          </Stack>

          <Stack
            spacing={0.5}
            sx={{
              alignItems: 'center',
              pt: 1,
            }}
          >
            <Tooltip title="Undo last decision">
              <span>
                <IconButton
                  aria-label="Undo last decision"
                  color="warning"
                  disabled={!lastAction || busy}
                  onClick={() => void undoLastAction()}
                  sx={{
                    bgcolor: 'background.paper',
                    border: 2,
                    borderColor: 'warning.main',
                    boxShadow: 3,
                    height: { xs: 48, sm: 54 },
                    transition:
                      'transform 160ms ease, background-color 160ms ease',
                    width: { xs: 48, sm: 54 },
                    '&:hover': {
                      bgcolor: 'warning.main',
                      color: 'warning.contrastText',
                      transform: 'scale(1.08)',
                    },
                  }}
                >
                  {undoing ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : (
                    <RotateCcw size={24} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
              }}
            >
              Undo
            </Typography>
          </Stack>

          <Stack
            spacing={0.5}
            sx={{
              alignItems: 'center',
            }}
          >
            <Tooltip title="Allow (Right arrow)">
              <span>
                <IconButton
                  aria-label="Allow image (Right arrow)"
                  color="success"
                  disabled={!currentItem || busy}
                  onClick={() => void applyDecision('allowed')}
                  sx={{
                    bgcolor: 'background.paper',
                    border: 2,
                    borderColor: 'success.main',
                    boxShadow: 4,
                    height: { xs: 58, sm: 68 },
                    transition:
                      'transform 160ms ease, background-color 160ms ease',
                    width: { xs: 58, sm: 68 },
                    '&:hover': {
                      bgcolor: 'success.main',
                      color: 'success.contrastText',
                      transform: 'scale(1.08)',
                    },
                  }}
                >
                  {saving && exitDirection === 'right' ? (
                    <CircularProgress color="inherit" size={24} />
                  ) : (
                    <Heart size={31} strokeWidth={2.4} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
              }}
            >
              Allow
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
